import { Check, Loader2, Circle } from 'lucide-react';

type QAStatus =
  | 'pending'
  | 'extracting'
  | 'evolving'
  | 'deepening'
  | 'generating'
  | 'completed'
  | 'failed';

type GenerationStage = 'planning' | 'drafting' | 'polishing' | 'checking' | null;

interface QAGenerationProgressProps {
  status: QAStatus;
  generationStage: GenerationStage;
}

interface Stage {
  id: string;
  label: string;
}

const STAGES: Stage[] = [
  { id: 'extract', label: 'Extract' },
  { id: 'planning', label: 'Plan' },
  { id: 'drafting', label: 'Draft' },
  { id: 'polishing', label: 'Polish' },
  { id: 'checking', label: 'Check' },
];

function getStageStatus(
  stageId: string,
  status: QAStatus,
  generationStage: GenerationStage
): 'completed' | 'current' | 'pending' {
  // Extract stage
  if (stageId === 'extract') {
    if (status === 'extracting') return 'current';
    if (
      status === 'evolving' ||
      status === 'deepening' ||
      status === 'generating' ||
      status === 'completed'
    ) {
      return 'completed';
    }
    return 'pending';
  }

  // Generation stages
  const genStageOrder: GenerationStage[] = ['planning', 'drafting', 'polishing', 'checking'];
  const stageIdx = genStageOrder.indexOf(stageId as GenerationStage);
  const currentIdx = generationStage ? genStageOrder.indexOf(generationStage) : -1;

  if (status === 'completed') return 'completed';
  if (status !== 'generating') return 'pending';
  if (stageIdx === -1) return 'pending';

  if (currentIdx === -1) {
    // No generation stage yet, but we're generating - first stage is current
    return stageIdx === 0 ? 'current' : 'pending';
  }

  if (stageIdx < currentIdx) return 'completed';
  if (stageIdx === currentIdx) return 'current';
  return 'pending';
}

export function QAGenerationProgress({ status, generationStage }: QAGenerationProgressProps) {
  return (
    <div className="bg-void/50 rounded-lg p-4">
      {/* Circles row with connectors */}
      <div className="flex items-center">
        {STAGES.map((stage, idx) => {
          const stageStatus = getStageStatus(stage.id, status, generationStage);
          const nextStageStatus =
            idx < STAGES.length - 1
              ? getStageStatus(STAGES[idx + 1].id, status, generationStage)
              : null;

          return (
            <div key={stage.id} className="flex items-center flex-1 last:flex-none">
              {/* Circle */}
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  transition-all duration-300
                  ${
                    stageStatus === 'completed'
                      ? 'bg-success/20 text-success'
                      : stageStatus === 'current'
                        ? 'bg-rune/20 text-rune'
                        : 'bg-stone/30 text-mist/50'
                  }
                `}
              >
                {stageStatus === 'completed' ? (
                  <Check size={14} />
                ) : stageStatus === 'current' ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Circle size={8} />
                )}
              </div>

              {/* Connector line */}
              {idx < STAGES.length - 1 && (
                <div
                  className={`
                    flex-1 h-0.5 mx-2 transition-all duration-300
                    ${
                      nextStageStatus !== 'pending'
                        ? 'bg-success/50'
                        : stageStatus === 'current'
                          ? 'bg-gradient-to-r from-rune/50 to-stone/30'
                          : 'bg-stone/30'
                    }
                  `}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Labels row */}
      <div className="flex mt-2">
        {STAGES.map((stage, idx) => {
          const stageStatus = getStageStatus(stage.id, status, generationStage);

          return (
            <div key={stage.id} className="flex-1 last:flex-none">
              <div
                className={`
                  text-xs text-center transition-colors duration-300
                  ${idx === STAGES.length - 1 ? 'w-8' : ''}
                  ${
                    stageStatus === 'completed'
                      ? 'text-success/70'
                      : stageStatus === 'current'
                        ? 'text-rune font-medium'
                        : 'text-mist/50'
                  }
                `}
              >
                {stage.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
